'use client';

import { useState, useEffect } from 'react';
import { StarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';

const ProjectStats = () => {
  const [stars, setStars] = useState(null);
  const [pulls, setPulls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch GitHub Stars
        const githubRes = await fetch('https://api.github.com/repos/BleedingXiko/GhostHub');
        if (!githubRes.ok) {
          throw new Error(`GitHub API error: ${githubRes.status}`);
        }
        const githubData = await githubRes.json();
        setStars(githubData.stargazers_count);

        // Try Docker Hub Registry API v2
        try {
          // This is the Docker Registry API v2 endpoint
          const dockerRes = await fetch('https://registry.hub.docker.com/v2/repositories/dhux/ghosthub');
          if (dockerRes.ok) {
            const dockerData = await dockerRes.json();
            setPulls(dockerData.pull_count || 717); // Fallback to 717 if pull_count is not available
          } else {
            console.warn(`Docker Hub API error: ${dockerRes.status}`);
            setPulls(717); // Fallback value
          }
        } catch (dockerErr) {
          console.warn('Docker Hub API error:', dockerErr);
          setPulls(717); // Fallback value
        }
        
      } catch (err) {
        console.error('Failed to fetch project stats:', err);
        setError(err.message);
        // Still try to show Docker pulls even if GitHub fails
        if (!pulls) setPulls(434);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-6 my-4 bg-dark-800 rounded-lg">
        <p className="text-ghost-300">Loading project stats...</p>
      </div>
    );
  }

  // Don't render the component at all if there's a critical error fetching both stats
  if (error && !stars && !pulls) {
     return null; // Hide completely on error
  }

  // Only render if we have at least one stat
  if (!stars && !pulls) return null; 

  return (
    <div className="py-8 bg-dark-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-12 py-4">
          {stars !== null && (
            <a 
              href="https://github.com/BleedingXiko/GhostHub/stargazers"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xl text-gray-200 hover:text-ghost-400 transition-colors duration-200"
            >
              <StarIcon className="h-7 w-7 text-yellow-400 mr-3" />
              <span><span className="font-bold">{Number(stars).toLocaleString()}</span> GitHub Stars</span>
            </a>
          )}
          {stars !== null && pulls !== null && (
            <div className="hidden sm:block h-8 w-px bg-ghost-700"></div> // Divider
          )}
          {pulls !== null && (
            <a 
              href="https://hub.docker.com/r/dhux/ghosthub"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xl text-gray-200 hover:text-ghost-400 transition-colors duration-200"
            >
              <ArrowDownTrayIcon className="h-7 w-7 text-ghost-400 mr-3" />
              <span><span className="font-bold">{Number(pulls).toLocaleString()}</span> Docker Pulls</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectStats;
